import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Field, FieldLabel } from "./ui/field";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  Plus,
  Trash2,
  GripVertical,
  Smartphone,
  FileText,
  Mail,
  User,
  Phone,
  Building2,
  MapPin,
  Briefcase,
  HelpCircle,
  ChevronDown,
  Shield,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  Sparkles,
  Image as ImageIcon,
  ExternalLink,
  PhoneCall,
  Info,
  MessageCircle,
  Send,
  X,
  Paperclip,
  AlertCircle,
  FileWarning,
} from "lucide-react";
import { AiGeneratingOverlay } from "@/components/ui/ai-generating-overlay";

// ============================================================
// Types
// ============================================================

type QuestionType =
  | "EMAIL"
  | "FULL_NAME"
  | "FIRST_NAME"
  | "LAST_NAME"
  | "PHONE"
  | "CITY"
  | "STATE"
  | "ZIP"
  | "COMPANY_NAME"
  | "JOB_TITLE"
  | "CUSTOM";

interface FormQuestion {
  id: string;
  type: QuestionType;
  key?: string;
  label?: string;
  options?: string[];
}

/**
 * Czesciowa aktualizacja formularza przysylana przez asystenta AI.
 * Kazde pole odpowiada jednemu setterowi stanu w applyFormUpdate ponizej.
 */
type FormUpdatePatch = {
  formName?: string;
  questions?: Array<{
    type: QuestionType;
    key?: string;
    label?: string;
    options?: string[];
  }>;
  contextCardTitle?: string;
  contextCardBody?: string;
  contextCardItems?: string[];
  coverImage?: Partial<CoverImageConfig>;
  coverText?: string;
  thankYouTitle?: string;
  thankYouBody?: string;
  thankYouButtonType?: "call" | "website" | "none";
  thankYouButtonText?: string;
};

interface CoverImageConfig {
  text: string;
  bgColor: string;
  textColor: string;
}

interface InstantFormCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pageId: string;
  pageName?: string;
  onFormCreated: (form: { id: string; name: string; status: string }) => void;
  clientId?: string;
  trackingParameters?: Record<string, string>;
}

interface ChatMessage {
  id: string;
  role: "user" | "ai";
  text: string;
  imagePreview?: string;
}

// ============================================================
// Constants
// ============================================================

const QUESTION_TYPES: {
  value: QuestionType;
  labelKey: string;
  placeholderKey: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "EMAIL",
    labelKey: "questionTypes.EMAIL",
    placeholderKey: "questionPlaceholders.EMAIL",
    icon: <Mail className="w-3.5 h-3.5" />,
  },
  {
    value: "FULL_NAME",
    labelKey: "questionTypes.FULL_NAME",
    placeholderKey: "questionPlaceholders.FULL_NAME",
    icon: <User className="w-3.5 h-3.5" />,
  },
  {
    value: "FIRST_NAME",
    labelKey: "questionTypes.FIRST_NAME",
    placeholderKey: "questionPlaceholders.FIRST_NAME",
    icon: <User className="w-3.5 h-3.5" />,
  },
  {
    value: "LAST_NAME",
    labelKey: "questionTypes.LAST_NAME",
    placeholderKey: "questionPlaceholders.LAST_NAME",
    icon: <User className="w-3.5 h-3.5" />,
  },
  {
    value: "PHONE",
    labelKey: "questionTypes.PHONE",
    placeholderKey: "questionPlaceholders.PHONE",
    icon: <Phone className="w-3.5 h-3.5" />,
  },
  {
    value: "COMPANY_NAME",
    labelKey: "questionTypes.COMPANY_NAME",
    placeholderKey: "questionPlaceholders.COMPANY_NAME",
    icon: <Building2 className="w-3.5 h-3.5" />,
  },
  {
    value: "JOB_TITLE",
    labelKey: "questionTypes.JOB_TITLE",
    placeholderKey: "questionPlaceholders.JOB_TITLE",
    icon: <Briefcase className="w-3.5 h-3.5" />,
  },
  {
    value: "CITY",
    labelKey: "questionTypes.CITY",
    placeholderKey: "questionPlaceholders.CITY",
    icon: <MapPin className="w-3.5 h-3.5" />,
  },
  {
    value: "CUSTOM",
    labelKey: "questionTypes.CUSTOM",
    placeholderKey: "questionPlaceholders.CUSTOM",
    icon: <HelpCircle className="w-3.5 h-3.5" />,
  },
];

const QUESTION_TYPE_MAP = new Map(QUESTION_TYPES.map((q) => [q.value, q]));

const INPUT_CLS =
  "h-9 text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30";
const LABEL_CLS = "text-xs text-white/60 mb-1";

const COVER_BG_COLORS = [
  { value: "#1877F2", labelKey: "coverBgColors.metaBlue" },
  { value: "#0F172A", labelKey: "coverBgColors.navy" },
  { value: "#18181B", labelKey: "coverBgColors.black" },
  { value: "#7C3AED", labelKey: "coverBgColors.purple" },
  { value: "#059669", labelKey: "coverBgColors.green" },
  { value: "#DC2626", labelKey: "coverBgColors.red" },
  { value: "#EA580C", labelKey: "coverBgColors.orange" },
];

// ============================================================
// Phone Preview Component
// ============================================================

function CoverHeader({
  coverImage,
  pageName,
}: {
  coverImage?: CoverImageConfig;
  pageName?: string;
}) {
  return (
    <div>
      {/* Cover image */}
      {coverImage?.text ? (
        <div
          className="w-full h-[120px] flex items-center justify-center px-6"
          style={{ backgroundColor: coverImage.bgColor || "#1877F2" }}
        >
          <p
            className="text-center font-bold leading-tight"
            style={{
              color: coverImage.textColor || "#FFFFFF",
              fontSize: coverImage.text.length > 40 ? "12px" : "14px",
            }}
          >
            {coverImage.text}
          </p>
        </div>
      ) : (
        <div className="w-full h-[60px]" style={{ backgroundColor: "#1877F2" }} />
      )}
      {/* Avatar overlapping cover/content */}
      <div className="flex flex-col items-center -mt-5">
        <div className="w-10 h-10 rounded-full bg-blue-600 border-2 border-white flex items-center justify-center shadow-md z-10">
          <span className="text-white text-sm font-bold">{(pageName || "S")[0].toUpperCase()}</span>
        </div>
        {pageName && (
          <span className="text-[10px] font-semibold text-gray-700 mt-1">{pageName}</span>
        )}
      </div>
    </div>
  );
}

function PhonePreview({
  formName,
  questions,
  contextCardTitle,
  contextCardContent,
  contextCardBody,
  thankYouTitle,
  thankYouBody,
  pageName,
  previewScreen,
  coverImage,
  thankYouButtonType,
  thankYouButtonText,
  thankYouUrl,
}: {
  formName: string;
  questions: FormQuestion[];
  contextCardTitle?: string;
  contextCardContent?: string[];
  contextCardBody?: string;
  thankYouTitle?: string;
  thankYouBody?: string;
  pageName?: string;
  previewScreen: "intro" | "form" | "thanks";
  coverImage?: CoverImageConfig;
  thankYouButtonType?: "call" | "website" | "none";
  thankYouButtonText?: string;
  thankYouUrl?: string;
}) {
  return (
    <div className="flex flex-col items-center">
      {/* Phone frame */}
      <div className="relative w-[280px] h-[560px] bg-black rounded-[40px] border-[3px] border-white/20 shadow-2xl shadow-black/50 overflow-hidden">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120px] h-[28px] bg-black rounded-b-2xl z-20" />

        {/* Status bar */}
        <div className="h-[44px] bg-white flex items-end justify-between px-6 pb-1">
          <span className="text-[10px] font-semibold text-black">9:41</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-2 border border-black/60 rounded-sm relative">
              <div className="absolute inset-[1px] right-[2px] bg-black/60 rounded-[1px]" />
            </div>
          </div>
        </div>

        {/* Header bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
          <ArrowLeft className="w-4 h-4 text-blue-600" />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
              <FileText className="w-3 h-3 text-white" />
            </div>
            <span className="text-xs font-semibold text-gray-900 truncate">
              {pageName || "Strona"}
            </span>
          </div>
        </div>

        {/* Content area */}
        <div className="h-[calc(100%-44px-41px)] bg-white overflow-y-auto scrollbar-hide">
          {previewScreen === "intro" && contextCardTitle && (
            <IntroScreen
              title={contextCardTitle}
              body={contextCardBody}
              content={contextCardContent}
              coverImage={coverImage}
              pageName={pageName}
            />
          )}
          {previewScreen === "form" && <FormScreen formName={formName} questions={questions} />}
          {previewScreen === "thanks" && (
            <ThankYouScreen
              title={thankYouTitle}
              body={thankYouBody}
              coverImage={coverImage}
              pageName={pageName}
              buttonType={thankYouButtonType}
              buttonText={thankYouButtonText}
              url={thankYouUrl}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function IntroScreen({
  title,
  body,
  content,
  coverImage,
  pageName,
}: {
  title: string;
  body?: string;
  content?: string[];
  coverImage?: CoverImageConfig;
  pageName?: string;
}) {
  const { t } = useTranslation("instantFormCreator");
  return (
    <div>
      <CoverHeader coverImage={coverImage} pageName={pageName} />
      <div className="p-4 pt-2">
        <h3 className="text-sm font-bold text-gray-900 mb-1">
          {title || t("preview.introDefault")}
        </h3>
        {body && (
          <p className="text-[11px] text-gray-600 leading-relaxed mb-3 whitespace-pre-line">
            {body}
          </p>
        )}
        {content && content.length > 0 && (
          <ul className="space-y-2 mb-3">
            {content.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
        <button className="w-full mt-2 py-2.5 rounded-lg bg-blue-600 text-white text-xs font-semibold">
          {t("preview.nextButton")}
        </button>
      </div>
    </div>
  );
}

function FormScreen({ formName, questions }: { formName: string; questions: FormQuestion[] }) {
  const { t } = useTranslation("instantFormCreator");
  return (
    <div className="p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-1">
        {formName || t("preview.formDefault")}
      </h3>
      <p className="text-[10px] text-gray-400 mb-3">{t("preview.fillFields")}</p>
      <div className="space-y-3">
        {questions.map((q) => {
          const typeInfo = QUESTION_TYPE_MAP.get(q.type);
          const label =
            q.type === "CUSTOM"
              ? q.label || t("preview.customQuestion")
              : typeInfo
                ? t(typeInfo.labelKey)
                : q.type;
          const hasOptions = q.options && q.options.length > 0;
          return (
            <div key={q.id}>
              <label className="text-[10px] font-medium text-gray-500 block mb-1">{label}</label>
              {hasOptions ? (
                <div className="space-y-1">
                  {q.options!.map((opt, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 h-7 rounded-md border border-gray-200 bg-gray-50 px-2.5"
                    >
                      <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                      <span className="text-[10px] text-gray-600">
                        {opt || t("preview.optionN", { n: i + 1 })}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-8 rounded-md border border-gray-200 bg-gray-50 px-2.5 flex items-center">
                  <span className="text-[10px] text-gray-300">
                    {typeInfo ? t(typeInfo.placeholderKey) : t("preview.answerPlaceholder")}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {questions.length === 0 && (
        <div className="py-8 text-center">
          <HelpCircle className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-[10px] text-gray-300">{t("preview.addQuestionsHint")}</p>
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-4 mb-3">
        <Shield className="w-3 h-3 text-gray-300" />
        <span className="text-[9px] text-gray-300">{t("preview.privacyPolicy")}</span>
      </div>
      <button className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-xs font-semibold">
        {t("preview.submitButton")}
      </button>
    </div>
  );
}

function ThankYouScreen({
  title,
  body,
  coverImage,
  pageName,
  buttonType,
  buttonText,
  url,
}: {
  title?: string;
  body?: string;
  coverImage?: CoverImageConfig;
  pageName?: string;
  buttonType?: "call" | "website" | "none";
  buttonText?: string;
  url?: string;
}) {
  const { t } = useTranslation("instantFormCreator");
  const defaultLabels: Record<string, string> = {
    call: t("preview.callNow"),
    website: t("preview.viewWebsite"),
  };
  const buttonConfig: Record<string, { icon: React.ReactNode; className: string }> = {
    call: { icon: <PhoneCall className="w-3 h-3" />, className: "bg-green-600 text-white" },
    website: { icon: <ExternalLink className="w-3 h-3" />, className: "bg-blue-600 text-white" },
  };
  const cfg = buttonType && buttonType !== "none" ? buttonConfig[buttonType] : null;
  const label = buttonText || (buttonType ? defaultLabels[buttonType] : "");
  const btn = cfg ? { ...cfg, label } : null;

  return (
    <div>
      <CoverHeader coverImage={coverImage} pageName={pageName} />
      <div className="p-4 pt-3 flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
        </div>
        <h3 className="text-sm font-bold text-gray-900 mb-2 text-center">
          {title || t("preview.thankYouDefault")}
        </h3>
        <p className="text-[11px] text-gray-500 text-center leading-relaxed mb-2">
          {body || t("preview.thankYouBodyDefault")}
        </p>
        <p className="text-[10px] text-gray-400 text-center mb-4">
          {t("preview.thankYouSent", { pageName: pageName || t("preview.advertiser") })}
        </p>
        {btn && (
          <button
            className={`w-full py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${btn.className}`}
          >
            {btn.icon} {btn.label}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export default function InstantFormCreator({
  open,
  onOpenChange,
  pageId,
  pageName,
  onFormCreated,
  clientId,
  trackingParameters,
}: InstantFormCreatorProps) {
  const { t } = useTranslation("instantFormCreator");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [formName, setFormName] = useState("");
  const [questions, setQuestions] = useState<FormQuestion[]>([
    { id: "1", type: "FULL_NAME" },
    { id: "2", type: "EMAIL" },
    { id: "3", type: "PHONE" },
  ]);
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");

  // Context card (intro screen)
  const [showContextCard, setShowContextCard] = useState(false);
  const [contextCardTitle, setContextCardTitle] = useState("");
  const [contextCardBody, setContextCardBody] = useState("");
  const [contextCardItems, setContextCardItems] = useState<string[]>([""]);

  // Thank you page
  const [thankYouTitle, setThankYouTitle] = useState(() => t("defaults.thankYouTitle"));
  const [thankYouBody, setThankYouBody] = useState(() => t("defaults.thankYouBody"));
  const [thankYouUrl, setThankYouUrl] = useState("");
  const [thankYouButtonType, setThankYouButtonType] = useState<"call" | "website" | "none">(
    "website"
  );
  const [thankYouButtonText, setThankYouButtonText] = useState("");
  const [thankYouPhoneNumber, setThankYouPhoneNumber] = useState("");
  const [thankYouCountryCode, setThankYouCountryCode] = useState("PL");
  const [thankYouPhonePrefix, setThankYouPhonePrefix] = useState("+48");
  // Preview screen toggle
  const [previewScreen, setPreviewScreen] = useState<"intro" | "form" | "thanks">("form");

  // Section collapse state — only required fields and questions are open by default
  const [expandIntro, setExpandIntro] = useState(false);
  // Thank-you section collapse (only the setter is read; value drives no render branch yet).
  const [, setExpandThanks] = useState(false);

  // AI generation
  const [aiGenerating, setAiGenerating] = useState(false);

  // Cover image for intro
  const [coverImage, setCoverImage] = useState<CoverImageConfig>({
    text: "",
    bgColor: "#1877F2",
    textColor: "#FFFFFF",
  });
  const [aiCtaOptions, setAiCtaOptions] = useState<string[]>([]);
  const [aiCtaLoading, setAiCtaLoading] = useState(false);

  // AI Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatImage, setChatImage] = useState<string | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const chatFileRef = useRef<HTMLInputElement>(null);

  const addQuestion = useCallback((type: QuestionType) => {
    setQuestions((prev) => [...prev, { id: String(Date.now()), type }]);
  }, []);

  const removeQuestion = useCallback((id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  }, []);

  const updateQuestion = useCallback((id: string, updates: Partial<FormQuestion>) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, ...updates } : q)));
  }, []);

  const addContextItem = useCallback(() => {
    setContextCardItems((prev) => [...prev, ""]);
  }, []);

  const updateContextItem = useCallback((index: number, value: string) => {
    setContextCardItems((prev) => prev.map((item, i) => (i === index ? value : item)));
  }, []);

  const removeContextItem = useCallback((index: number) => {
    setContextCardItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetForm = useCallback(() => {
    setFormName("");
    setQuestions([
      { id: "1", type: "FULL_NAME" },
      { id: "2", type: "EMAIL" },
      { id: "3", type: "PHONE" },
    ]);
    setPrivacyPolicyUrl("");
    setShowContextCard(false);
    setContextCardTitle("");
    setContextCardBody("");
    setContextCardItems([""]);
    setThankYouTitle(t("defaults.thankYouTitle"));
    setThankYouBody(t("defaults.thankYouBody"));
    setThankYouUrl("");
    setThankYouButtonType("website");
    setThankYouButtonText("");
    setThankYouPhoneNumber("");
    setThankYouCountryCode("PL");
    setThankYouPhonePrefix("+48");
    setPreviewScreen("form");
    setCoverImage({ text: "", bgColor: "#1877F2", textColor: "#FFFFFF" });
    setAiCtaOptions([]);
    setExpandIntro(false);
    setExpandThanks(false);
    setChatMessages([]);
    setChatInput("");
    setChatImage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply partial form update from AI chat
  const applyFormUpdate = useCallback((update: FormUpdatePatch) => {
    if (update.formName !== undefined) setFormName(update.formName);
    if (update.questions?.length) {
      const mapped: FormQuestion[] = update.questions.map((q, i) => ({
        id: String(Date.now() + i),
        type: q.type as QuestionType,
        ...(q.type === "CUSTOM"
          ? {
              key: q.key || `custom_${i}`,
              label: q.label || "",
              options: q.options || [],
            }
          : {}),
      }));
      setQuestions(mapped);
    }
    if (update.contextCardTitle !== undefined) {
      setShowContextCard(true);
      setExpandIntro(true);
      setContextCardTitle(update.contextCardTitle);
    }
    if (update.contextCardBody !== undefined) setContextCardBody(update.contextCardBody);
    if (update.contextCardItems) setContextCardItems(update.contextCardItems);
    if (update.coverImage) setCoverImage((prev) => ({ ...prev, ...update.coverImage }));
    if (update.coverText) {
      // przepisane do zmiennej, bo w domknieciu settera TS gubi zawezenie z ifa
      const coverText = update.coverText;
      setCoverImage((prev) => ({ ...prev, text: coverText }));
    }
    if (update.thankYouTitle !== undefined) setThankYouTitle(update.thankYouTitle);
    if (update.thankYouBody !== undefined) setThankYouBody(update.thankYouBody);
    if (update.thankYouButtonType) setThankYouButtonType(update.thankYouButtonType);
    if (update.thankYouButtonText !== undefined) setThankYouButtonText(update.thankYouButtonText);
  }, []);

  // Handle image paste/file for chat
  const handleChatImageFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setChatImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  // Send chat message
  const sendChatMessage = useCallback(async () => {
    const text = chatInput.trim();
    if (!text && !chatImage) return;

    const userMsg: ChatMessage = {
      id: String(Date.now()),
      role: "user",
      text: text || t("chat.imageAttachment"),
      imagePreview: chatImage || undefined,
    };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput("");
    const imageToSend = chatImage;
    setChatImage(null);
    setChatLoading(true);

    try {
      const currentForm = {
        formName,
        questions: questions.map((q) => ({
          type: q.type,
          ...(q.type === "CUSTOM" ? { key: q.key, label: q.label, options: q.options } : {}),
        })),
        contextCardTitle,
        contextCardBody,
        contextCardItems: contextCardItems.filter(Boolean),
        coverImage,
        thankYouTitle,
        thankYouBody,
        thankYouButtonType,
        thankYouButtonText,
      };

      const result = await apiRequest("POST", "/api/campaign-creator/form-chat", {
        message: text,
        imageBase64: imageToSend || undefined,
        currentForm,
      });

      const aiMsg: ChatMessage = {
        id: String(Date.now() + 1),
        role: "ai",
        text: result.response || t("chat.noAiResponse"),
      };
      setChatMessages((prev) => [...prev, aiMsg]);

      if (result.formUpdate) {
        applyFormUpdate(result.formUpdate);
        toast({ title: t("toasts.formUpdatedTitle"), description: t("toasts.formUpdatedDesc") });
      }
    } catch (err) {
      const errMsg: ChatMessage = {
        id: String(Date.now() + 1),
        role: "ai",
        text: `${t("chat.errorPrefix")}${(err as Error).message || t("chat.connectionError")}`,
      };
      setChatMessages((prev) => [...prev, errMsg]);
    } finally {
      setChatLoading(false);
    }
  }, [
    chatInput,
    chatImage,
    formName,
    questions,
    contextCardTitle,
    contextCardBody,
    contextCardItems,
    coverImage,
    thankYouTitle,
    thankYouBody,
    thankYouButtonType,
    thankYouButtonText,
    applyFormUpdate,
    toast,
    t,
  ]);

  // Add option to a CUSTOM question (for multiple-choice)
  const addOptionToQuestion = useCallback((questionId: string) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, options: [...(q.options || []), ""] } : q))
    );
  }, []);

  const updateOption = useCallback((questionId: string, index: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, options: (q.options || []).map((o, i) => (i === index ? value : o)) }
          : q
      )
    );
  }, []);

  const removeOption = useCallback((questionId: string, index: number) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId ? { ...q, options: (q.options || []).filter((_, i) => i !== index) } : q
      )
    );
  }, []);

  // AI CTA generation for cover image
  const generateCtaOptions = useCallback(async () => {
    if (!clientId) return;
    setAiCtaLoading(true);
    try {
      const result = await apiRequest("POST", "/api/campaign-creator/generate-form-cta", {
        clientId,
      });
      if (result.options?.length) {
        setAiCtaOptions(result.options);
      }
    } catch (err) {
      toast({
        title: t("toasts.retryTitle"),
        description: (err as Error)?.message || t("toasts.retryDesc"),
        variant: "destructive",
      });
    } finally {
      setAiCtaLoading(false);
    }
  }, [clientId, toast, t]);

  const generateFromOfferSheet = useCallback(async () => {
    if (!clientId) return;
    setAiGenerating(true);
    try {
      const result = await apiRequest("POST", "/api/campaign-creator/generate-form-questions", {
        clientId,
      });

      // Apply form name
      if (result.formName) setFormName(result.formName);

      // Apply generated questions
      if (result.questions?.length) {
        const mapped: FormQuestion[] = result.questions.map(
          (q: { type: string; key?: string; label?: string; options?: string[] }, i: number) => ({
            id: String(Date.now() + i),
            type: q.type as QuestionType,
            ...(q.type === "CUSTOM"
              ? {
                  key: q.key || `custom_${i}`,
                  label: q.label || "",
                  options: q.options || [],
                }
              : {}),
          })
        );
        setQuestions(mapped);
      }

      // Apply context card (intro screen)
      if (result.contextCardTitle) {
        setShowContextCard(true);
        setExpandIntro(true);
        setContextCardTitle(result.contextCardTitle);
        setContextCardBody(result.contextCardBody || "");
        setContextCardItems(result.contextCardItems?.length ? result.contextCardItems : [""]);
        setPreviewScreen("intro");
      }

      if (result.coverText) {
        setCoverImage((prev) => ({ ...prev, text: result.coverText }));
      }

      if (result.thankYouTitle) setThankYouTitle(result.thankYouTitle);
      if (result.thankYouBody) setThankYouBody(result.thankYouBody);
      if (result.thankYouButtonType) setThankYouButtonType(result.thankYouButtonType);
      if (result.thankYouButtonText) setThankYouButtonText(result.thankYouButtonText);
      if (result.thankYouTitle || result.thankYouBody) setExpandThanks(true);

      toast({
        title: t("toasts.generatedTitle"),
        description: t("toasts.generatedDesc", { count: result.questions?.length || 0 }),
      });
    } catch (err) {
      toast({
        title: t("toasts.retryTitle"),
        description: (err as Error)?.message || t("toasts.retryDesc"),
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
    }
  }, [clientId, toast, t]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages, chatLoading]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        pageId,
        name: formName,
        questions: questions.map((q) => ({
          type: q.type,
          ...(q.type === "CUSTOM"
            ? {
                key: q.key || "custom",
                label: q.label || "",
                ...(q.options?.length
                  ? {
                      options: q.options
                        .filter(Boolean)
                        .map((v, i) => ({ value: v, key: `key_${i}` })),
                    }
                  : {}),
              }
            : {}),
        })),
        privacyPolicyUrl,
        ...(showContextCard && contextCardTitle
          ? {
              contextCardTitle,
              contextCardContent: contextCardItems.filter(Boolean),
            }
          : {}),
        ...(coverImage.text
          ? {
              coverText: coverImage.text,
              coverBgColor: coverImage.bgColor,
              coverTextColor: coverImage.textColor,
            }
          : {}),
        thankYouTitle: thankYouTitle || undefined,
        thankYouBody: thankYouBody || undefined,
        thankYouButtonType,
        ...(thankYouButtonType === "website"
          ? {
              thankYouUrl,
              thankYouButtonText: thankYouButtonText || t("preview.viewWebsite"),
            }
          : {
              thankYouPhoneNumber: thankYouPhoneNumber.replace(/\s/g, ""),
              thankYouPhonePrefix: thankYouPhonePrefix,
              thankYouCountryCode: thankYouCountryCode.trim(),
              thankYouButtonText: thankYouButtonText || t("preview.callNow"),
            }),
        ...(trackingParameters && Object.keys(trackingParameters).length > 0
          ? { trackingParameters }
          : {}),
      };
      return apiRequest("POST", "/api/campaign-creator/leadgen-forms", payload);
    },
    onSuccess: (data: { form: { id: string; name: string; status: string } }) => {
      onOpenChange(false);
      resetForm();
      toast({
        title: t("toasts.formCreatedTitle"),
        description: t("toasts.formCreatedDesc", { name: data.form.name }),
      });
      queryClient.invalidateQueries({ queryKey: ["campaign-creator-leadgen-forms", pageId] });
      onFormCreated(data.form);
    },
    onError: (error: Error) => {
      toast({
        title: t("toasts.retryTitle"),
        description: error?.message || t("toasts.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const isValidUrl = (url: string) => /^https?:\/\/.+\..+/.test(url);
  const isValidPhone = (phone: string) => /^[\d\s\-()]{6,15}$/.test(phone.trim());
  const isValidCountryCode = (code: string) => /^[A-Z]{2}$/.test(code.trim());

  const thankYouValid =
    thankYouButtonType === "website"
      ? isValidUrl(thankYouUrl)
      : isValidPhone(thankYouPhoneNumber) && isValidCountryCode(thankYouCountryCode);
  const canSubmit =
    formName &&
    questions.length > 0 &&
    isValidUrl(privacyPolicyUrl) &&
    thankYouValid &&
    !createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[900px] w-[95vw] bg-[#0a0c10] border-white/[0.1] text-white p-0 gap-0 overflow-hidden max-h-[90vh]">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/[0.06]">
          <DialogTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            {t("title")}
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            {t("description", { pageName: pageName || t("form.facebookDefault") })}
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex flex-col md:flex-row overflow-hidden"
          style={{ height: "calc(90vh - 120px)", maxHeight: "680px" }}
        >
          {/* Left — Form config */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5 border-r border-white/[0.06]">
            {/* ── HERO: AI generation from offer sheet ── */}
            {clientId && (
              <div className="relative rounded-xl border border-purple-500/30 bg-gradient-to-br from-purple-600/15 via-fuchsia-600/10 to-cyan-600/15 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-purple-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-white">{t("hero.title")}</h3>
                    <p className="text-[11px] text-white/60 leading-relaxed mt-0.5">
                      {t("hero.description")}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={generateFromOfferSheet}
                  disabled={aiGenerating}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 text-white hover:from-purple-500 hover:to-cyan-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
                >
                  {aiGenerating ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("form.generating")}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5" /> {t("form.generateFromOffer")}
                    </>
                  )}
                </button>
                {aiGenerating && (
                  <AiGeneratingOverlay compact message={t("form.generatingOverlay")} />
                )}
              </div>
            )}

            {!clientId && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 flex items-start gap-2">
                <FileWarning className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-200/80 leading-relaxed">
                  {t("hero.noClient")}
                </p>
              </div>
            )}

            {/* ── 1. WYMAGANE ── */}
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold flex items-center justify-center">
                  1
                </span>
                <Label className="text-xs font-semibold text-white">{t("section.required")}</Label>
                <span className="text-[10px] text-red-400">*</span>
              </div>
              <Field>
                <FieldLabel>{t("form.formNameLabel")}</FieldLabel>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={t("form.formNamePlaceholder")}
                  className={INPUT_CLS}
                />
              </Field>
              <Field>
                <FieldLabel>
                  {t("form.privacyPolicyLabel")}
                  <span className="text-red-400 ml-0.5" title="Pole wymagane">
                    *
                  </span>
                </FieldLabel>
                <Input
                  value={privacyPolicyUrl}
                  onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
                  placeholder={t("form.privacyPolicyPlaceholder")}
                  required
                  className={`${INPUT_CLS} ${!privacyPolicyUrl || !isValidUrl(privacyPolicyUrl) ? "border-amber-500/40" : ""}`}
                />
                {(!privacyPolicyUrl || !isValidUrl(privacyPolicyUrl)) && (
                  <p className="text-[10px] text-amber-400/80 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />{" "}
                    {!privacyPolicyUrl
                      ? t("form.privacyPolicyError")
                      : "Podaj prawidłowy URL (https://...)"}
                  </p>
                )}
              </Field>
            </div>

            {/* ── 2. PYTANIA ── */}
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold flex items-center justify-center">
                  2
                </span>
                <Label className="text-xs font-semibold text-white">{t("section.questions")}</Label>
              </div>
              <p className="text-[10px] text-white/40 -mt-1">{t("section.questionsHint")}</p>

              <div className="space-y-3">
                {questions.map((q, i) => {
                  const typeInfo = QUESTION_TYPE_MAP.get(q.type);
                  return (
                    <div key={q.id} className="space-y-1">
                      <div className="flex items-center gap-2 group">
                        <GripVertical className="w-3.5 h-3.5 text-white/20 flex-shrink-0" />
                        <div className="flex items-center gap-2 flex-1 min-w-0 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2">
                          <span className="text-cyan-400/70 flex-shrink-0">{typeInfo?.icon}</span>
                          {q.type === "CUSTOM" ? (
                            <Input
                              value={q.label || ""}
                              onChange={(e) => updateQuestion(q.id, { label: e.target.value })}
                              placeholder={t("form.customQuestionPlaceholder")}
                              className="h-6 text-xs bg-transparent border-none p-0 text-white placeholder:text-white/30 focus-visible:ring-0"
                            />
                          ) : (
                            <span className="text-xs text-white/70">
                              {typeInfo ? t(typeInfo.labelKey) : q.type}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeQuestion(q.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-500/20 rounded"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>

                      {q.type === "CUSTOM" && (
                        <div className="ml-7 space-y-1.5">
                          {(!q.options || q.options.length === 0) && (
                            <p className="text-[10px] text-white/30 italic">
                              {t("form.openQuestionHint")}
                            </p>
                          )}
                          {(q.options || []).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded-full border border-white/20 flex-shrink-0" />
                              <Input
                                value={opt}
                                onChange={(e) => updateOption(q.id, oi, e.target.value)}
                                placeholder={t("form.optionN", { n: oi + 1 })}
                                className="h-7 text-[11px] bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/20 flex-1"
                              />
                              <button
                                type="button"
                                onClick={() => removeOption(q.id, oi)}
                                className="p-1 hover:bg-red-500/20 rounded text-white/20 hover:text-red-400"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addOptionToQuestion(q.id)}
                            className="text-[10px] text-cyan-400/60 hover:text-cyan-400 flex items-center gap-1 ml-4"
                          >
                            <Plus className="w-3 h-3" /> {t("form.addOption")}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Select onValueChange={(v) => addQuestion(v as QuestionType)}>
                <SelectTrigger className="h-8 text-xs bg-white/[0.03] border-dashed border-white/[0.12] text-white/40 hover:text-white/60 hover:border-white/[0.2]">
                  <div className="flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" />
                    <span>{t("form.addQuestion")}</span>
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                  {QUESTION_TYPES.map((qt) => (
                    <SelectItem
                      key={qt.value}
                      value={qt.value}
                      className="text-white hover:bg-white/[0.08] text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-400/70">{qt.icon}</span>
                        {t(qt.labelKey)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Ads Manager disqualification banner — visible if any CUSTOM question has options */}
              {questions.some(
                (q) => q.type === "CUSTOM" && (q.options?.filter(Boolean).length || 0) > 0
              ) && (
                <div className="rounded-lg border border-blue-500/20 bg-blue-500/[0.06] p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-300 flex-shrink-0 mt-0.5" />
                  <div className="text-[11px] text-blue-100/80 leading-relaxed">
                    <p className="font-medium text-blue-100 mb-0.5">
                      {t("section.adsManagerTitle")}
                    </p>
                    <p>{t("section.adsManagerBody")}</p>
                  </div>
                </div>
              )}
            </div>

            {/* ── 3. EKRAN POWITALNY (collapsible) ── */}
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02]">
              <button
                type="button"
                onClick={() => setExpandIntro((v) => !v)}
                className="w-full flex items-center gap-2 p-4 text-left hover:bg-white/[0.02] transition-colors rounded-xl"
              >
                <span className="w-5 h-5 rounded-full bg-white/[0.06] text-white/60 text-[10px] font-bold flex items-center justify-center">
                  3
                </span>
                <Label className="text-xs font-semibold text-white cursor-pointer flex-1">
                  {t("section.intro")}
                </Label>
                {showContextCard && contextCardTitle && (
                  <span className="text-[10px] text-cyan-400/80">
                    {thankYouUrl ? (
                      t("section.configured")
                    ) : (
                      <span className="text-orange-400/80">{t("form.required") ?? "Wymagane"}</span>
                    )}
                  </span>
                )}
                <ChevronDown
                  className={`w-4 h-4 text-white/40 transition-transform ${expandIntro ? "rotate-180" : ""}`}
                />
              </button>
              {expandIntro && (
                <div className="px-4 pb-4 space-y-3 border-t border-white/[0.04] pt-3">
                  <p className="text-[10px] text-white/40">{t("section.introHint")}</p>
                  <div className="flex items-center justify-between">
                    <Label className="text-[11px] text-white/60">
                      {t("form.introScreenLabel")}
                    </Label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowContextCard(!showContextCard);
                        if (!showContextCard) setPreviewScreen("intro");
                      }}
                      className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${
                        showContextCard
                          ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                          : "bg-white/[0.04] text-white/40 border-white/[0.1] hover:text-white/60"
                      }`}
                    >
                      {showContextCard ? t("form.introEnabled") : t("form.introDisabled")}
                    </button>
                  </div>
                  {showContextCard && (
                    <>
                      <Input
                        value={contextCardTitle}
                        onChange={(e) => setContextCardTitle(e.target.value)}
                        placeholder={t("form.introTitlePlaceholder")}
                        className={INPUT_CLS}
                      />
                      <Textarea
                        value={contextCardBody}
                        onChange={(e) => setContextCardBody(e.target.value)}
                        placeholder={t("form.introBodyPlaceholder")}
                        className="text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 min-h-[60px] resize-none"
                      />
                      <div className="space-y-2">
                        {contextCardItems.map((item, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400/50 flex-shrink-0" />
                            <Input
                              value={item}
                              onChange={(e) => updateContextItem(i, e.target.value)}
                              placeholder={t("form.introBulletN", { n: i + 1 })}
                              className="h-8 text-xs bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30"
                            />
                            {contextCardItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeContextItem(i)}
                                className="p-1 hover:bg-red-500/20 rounded"
                              >
                                <Trash2 className="w-3 h-3 text-red-400/60" />
                              </button>
                            )}
                          </div>
                        ))}
                        {contextCardItems.length < 5 && (
                          <button
                            type="button"
                            onClick={addContextItem}
                            className="text-[10px] text-cyan-400/60 hover:text-cyan-400 flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" /> {t("form.addBullet")}
                          </button>
                        )}
                      </div>

                      {/* Cover image — nested inside intro section */}
                      <div className="border-t border-white/[0.04] pt-3 mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <ImageIcon className="w-3.5 h-3.5 text-purple-400" />
                          <Label className="text-[11px] text-white/60">
                            {t("form.coverImageLabel")}
                          </Label>
                        </div>
                        {clientId && (
                          <>
                            <button
                              type="button"
                              onClick={generateCtaOptions}
                              disabled={aiCtaLoading}
                              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-[11px] font-medium bg-gradient-to-r from-purple-600/15 to-pink-600/15 text-purple-300 border border-purple-500/20 hover:from-purple-600/25 hover:to-pink-600/25 transition-all disabled:opacity-50 mb-2"
                            >
                              {aiCtaLoading ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Sparkles className="w-3 h-3" />
                              )}
                              {aiCtaLoading ? t("form.generatingCta") : t("form.generateCta")}
                            </button>
                            {aiCtaLoading && (
                              <AiGeneratingOverlay compact message={t("form.ctaOverlay")} />
                            )}
                            {aiCtaOptions.length > 0 && (
                              <div className="space-y-1.5 mb-2">
                                {aiCtaOptions.map((cta, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() =>
                                      setCoverImage((prev) => ({ ...prev, text: cta }))
                                    }
                                    className={`w-full text-left px-3 py-2 rounded-lg text-[11px] border transition-colors ${
                                      coverImage.text === cta
                                        ? "bg-purple-500/20 text-purple-200 border-purple-500/40"
                                        : "bg-white/[0.03] text-white/60 border-white/[0.08] hover:bg-white/[0.06]"
                                    }`}
                                  >
                                    {i + 1}. {cta}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                        <Input
                          value={coverImage.text}
                          onChange={(e) =>
                            setCoverImage((prev) => ({ ...prev, text: e.target.value }))
                          }
                          placeholder={t("form.coverTextPlaceholder")}
                          className={INPUT_CLS}
                        />
                        <Label className="text-[10px] text-white/40 mb-1 mt-2 block">
                          {t("form.bgColorLabel")}
                        </Label>
                        <div className="flex flex-wrap gap-1.5">
                          {COVER_BG_COLORS.map((c) => (
                            <button
                              key={c.value}
                              type="button"
                              onClick={() =>
                                setCoverImage((prev) => ({ ...prev, bgColor: c.value }))
                              }
                              className={`w-7 h-7 rounded-lg border-2 transition-transform ${
                                coverImage.bgColor === c.value
                                  ? "border-white scale-110"
                                  : "border-transparent hover:scale-105"
                              }`}
                              style={{ backgroundColor: c.value }}
                              title={t(c.labelKey)}
                            />
                          ))}
                        </div>
                        {coverImage.text && (
                          <div
                            className="w-full h-[80px] rounded-lg flex items-center justify-center px-4 mt-2"
                            style={{ backgroundColor: coverImage.bgColor }}
                          >
                            <p
                              className="text-center font-bold text-sm leading-tight"
                              style={{ color: coverImage.textColor }}
                            >
                              {coverImage.text}
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── 4. STRONA PODZIĘKOWANIA (collapsible) ── */}
            {/* ── 4. STRONA PODZIĘKOWANIA ── */}
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold flex items-center justify-center">
                  4
                </span>
                <Label className="text-xs font-semibold text-white">{t("section.thanks")}</Label>
                <span className="text-[10px] text-red-400">*</span>
              </div>
              <Input
                value={thankYouTitle}
                onChange={(e) => setThankYouTitle(e.target.value)}
                placeholder={t("form.thankYouTitlePlaceholder")}
                className={INPUT_CLS}
              />
              <Textarea
                value={thankYouBody}
                onChange={(e) => setThankYouBody(e.target.value)}
                placeholder={t("form.thankYouBodyPlaceholder")}
                className="text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 min-h-[60px] resize-none"
              />
              <div>
                <Label className="text-[10px] text-white/40 mb-1.5 block">
                  {t("form.thankYouButtonLabel")} <span className="text-red-400">*</span>
                </Label>
                <div className="flex gap-2">
                  {[
                    {
                      value: "call" as const,
                      labelKey: "form.btnCall",
                      icon: <PhoneCall className="w-3 h-3" />,
                    },
                    {
                      value: "website" as const,
                      labelKey: "form.btnWebsite",
                      icon: <ExternalLink className="w-3 h-3" />,
                    },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setThankYouButtonType(opt.value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium border transition-colors ${
                        thankYouButtonType === opt.value
                          ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                          : "bg-white/[0.03] text-white/40 border-white/[0.08] hover:text-white/60"
                      }`}
                    >
                      {opt.icon} {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
              {thankYouButtonType === "call" && (
                <>
                  <Input
                    value={thankYouButtonText}
                    onChange={(e) => setThankYouButtonText(e.target.value)}
                    placeholder={t("form.callNowPlaceholder")}
                    className={INPUT_CLS}
                  />
                  <div className="flex gap-2">
                    <div className="w-24">
                      <Input
                        value={thankYouPhonePrefix}
                        onChange={(e) => setThankYouPhonePrefix(e.target.value)}
                        placeholder="+48"
                        className={INPUT_CLS}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={thankYouPhoneNumber}
                        onChange={(e) => setThankYouPhoneNumber(e.target.value)}
                        placeholder="777 888 999"
                        className={INPUT_CLS}
                      />
                      {thankYouPhoneNumber && !isValidPhone(thankYouPhoneNumber) && (
                        <p className="text-[10px] text-amber-400/80 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Nieprawidłowy numer telefonu
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
              {thankYouButtonType === "website" && (
                <>
                  <Input
                    value={thankYouUrl}
                    onChange={(e) => setThankYouUrl(e.target.value)}
                    placeholder={t("form.thankYouUrlPlaceholder")}
                    className={INPUT_CLS}
                  />
                  {thankYouUrl && !isValidUrl(thankYouUrl) && (
                    <p className="text-[10px] text-amber-400/80 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Podaj prawidłowy URL (https://...)
                    </p>
                  )}
                </>
              )}
            </div>

            {/* AI Chat panel */}
            <div className="border-t border-white/[0.06] pt-4">
              <button
                type="button"
                onClick={() => setChatOpen(!chatOpen)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${
                  chatOpen
                    ? "bg-purple-600/20 text-purple-300 border border-purple-500/30"
                    : "bg-white/[0.04] text-white/50 border border-white/[0.08] hover:text-white/70"
                }`}
              >
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-3.5 h-3.5" />
                  Czat AI — popraw formularz
                </div>
                <span className="text-[10px] text-white/30">
                  {chatOpen ? t("form.aiChatCollapse") : t("form.aiChatExpand")}
                </span>
              </button>

              {chatOpen && (
                <div className="mt-3 rounded-lg border border-white/[0.08] bg-white/[0.02] overflow-hidden">
                  {/* Chat message list */}
                  <div
                    ref={chatScrollRef}
                    className="max-h-[200px] overflow-y-auto p-3 space-y-2.5"
                  >
                    {chatMessages.length === 0 && (
                      <p className="text-[10px] text-white/20 text-center py-4">
                        {t("form.aiChatEmpty")}
                      </p>
                    )}
                    {chatMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
                      >
                        {msg.imagePreview && (
                          <img
                            src={msg.imagePreview}
                            alt={t("chat.imageAttachment")}
                            className="w-24 h-24 object-cover rounded-lg mb-1 border border-white/[0.1]"
                          />
                        )}
                        <div
                          className={`max-w-[85%] px-3 py-2 rounded-lg text-[11px] leading-relaxed ${
                            msg.role === "user"
                              ? "bg-blue-600/30 text-blue-100"
                              : "bg-white/[0.06] text-white/70"
                          }`}
                        >
                          {msg.text}
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex items-start">
                        <div className="px-3 py-2 rounded-lg bg-white/[0.06] text-white/40 text-[11px] flex items-center gap-1.5">
                          <Loader2 className="w-3 h-3 animate-spin" /> {t("form.aiThinking")}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Image preview */}
                  {chatImage && (
                    <div className="px-3 pb-2">
                      <div className="relative inline-block">
                        <img
                          src={chatImage}
                          alt={t("chat.imageToSend")}
                          className="w-16 h-16 object-cover rounded-lg border border-white/[0.15]"
                        />
                        <button
                          type="button"
                          onClick={() => setChatImage(null)}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
                        >
                          <X className="w-2.5 h-2.5 text-white" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Chat input */}
                  <div className="flex items-center gap-2 px-3 py-2.5 border-t border-white/[0.06]">
                    <input
                      ref={chatFileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleChatImageFile(file);
                        e.target.value = "";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => chatFileRef.current?.click()}
                      className="p-1.5 rounded-md text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors"
                      title={t("form.aiChatAddImage")}
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                    </button>
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendChatMessage();
                        }
                      }}
                      onPaste={(e) => {
                        const items = e.clipboardData?.items;
                        if (!items) return;
                        for (let i = 0; i < items.length; i++) {
                          if (items[i].type.startsWith("image/")) {
                            const file = items[i].getAsFile();
                            if (file) handleChatImageFile(file);
                            break;
                          }
                        }
                      }}
                      placeholder={t("form.aiChatPlaceholder")}
                      className="flex-1 h-7 text-[11px] bg-transparent border-none text-white placeholder:text-white/20 focus:outline-none"
                      disabled={chatLoading}
                    />
                    <button
                      type="button"
                      onClick={sendChatMessage}
                      disabled={chatLoading || (!chatInput.trim() && !chatImage)}
                      className="p-1.5 rounded-md bg-purple-600/30 text-purple-300 hover:bg-purple-600/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right — Phone preview */}
          <div className="w-full md:w-[340px] flex-shrink-0 flex flex-col items-center justify-center bg-gradient-to-b from-white/[0.02] to-transparent p-5">
            {/* Screen toggle buttons */}
            <div className="flex items-center gap-1 mb-4 bg-white/[0.04] rounded-lg p-1 border border-white/[0.08]">
              {[
                { key: "intro" as const, labelKey: "form.screenIntro", show: showContextCard },
                { key: "form" as const, labelKey: "form.screenForm", show: true },
                { key: "thanks" as const, labelKey: "form.screenThanks", show: true },
              ]
                .filter((s) => s.show)
                .map((screen) => (
                  <button
                    key={screen.key}
                    type="button"
                    onClick={() => setPreviewScreen(screen.key)}
                    className={`px-3 py-1.5 rounded-md text-[10px] font-medium transition-colors ${
                      previewScreen === screen.key
                        ? "bg-cyan-500/20 text-cyan-400"
                        : "text-white/40 hover:text-white/60"
                    }`}
                  >
                    {t(screen.labelKey)}
                  </button>
                ))}
            </div>

            <PhonePreview
              formName={formName}
              questions={questions}
              contextCardTitle={contextCardTitle}
              contextCardContent={contextCardItems.filter(Boolean)}
              contextCardBody={contextCardBody}
              thankYouTitle={thankYouTitle}
              thankYouBody={thankYouBody}
              pageName={pageName}
              previewScreen={previewScreen}
              coverImage={showContextCard ? coverImage : undefined}
              thankYouButtonType={thankYouButtonType}
              thankYouButtonText={thankYouButtonText}
              thankYouUrl={thankYouUrl}
            />

            <div className="flex items-center gap-1.5 mt-3 text-white/20">
              <Smartphone className="w-3.5 h-3.5" />
              <span className="text-[10px]">{t("form.previewHint")}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-between">
          <p className="text-[10px] text-white/30">
            {t("form.footerNote", { pageName: pageName || t("form.facebookDefault") })}
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-9 text-xs text-white/50 hover:text-white/80"
            >
              {t("form.cancelButton")}
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit}
              className="h-9 text-xs bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white border-0"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> {t("form.creatingButton")}
                </>
              ) : (
                <>
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> {t("form.createButton")}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
